import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PlugZap, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

const TONES = {
    green: { dot: "bg-emerald-400", text: "text-emerald-300", label: "Connected" },
    amber: { dot: "bg-amber-400", text: "text-amber-300", label: "Needs attention" },
    red: { dot: "bg-red-500", text: "text-red-300", label: "Not reachable" },
    off: { dot: "bg-white/30", text: "text-white/50", label: "Not configured" },
};

const NOT_CONFIGURED = {
    hub: "No connection is linked to “This hub” on the Connections page — MongoDB tools are off here.",
    spoke: "The Radar hub has not sent this server its MongoDB connection yet — MongoDB tools are off here.",
    unlinked: "This environment is not linked to the Radar hub, so it has no MongoDB connection — MongoDB tools are off here.",
};

export function mcpState(s) {
    if (!s || !s.configured) return { tone: "off", reason: NOT_CONFIGURED[s?.role] || NOT_CONFIGURED.hub };
    if (!s.reachable) return { tone: "red", reason: s.error || "The MCP server did not answer." };
    if (!(s.map?.mapped + s.map?.shared)) return { tone: "amber", reason: "No collection has a confirmed company field yet — tenants can read nothing." };
    if (s.open_alerts) return { tone: "amber", reason: `${s.open_alerts} withheld result(s) waiting for review.` };
    return { tone: "green", reason: `${s.map.mapped} mapped · ${s.map.shared} shared · ${s.map.hidden} hidden collections.` };
}

export default function McpStatusCard({ status, onRefresh, isSuper }) {
    const [test, setTest] = useState(null);
    const [busy, setBusy] = useState(false);
    const st = mcpState(status);
    const tone = TONES[st.tone];
    const runTest = async () => {
        setBusy(true);
        try { setTest((await api.post("/admin/mongo-mcp/test/list-databases")).data); }
        catch (e) { setTest(null); toast.error(formatApiErrorDetail(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6" data-testid="mcp-status-card">
            <div className="flex items-start gap-4">
                <PlugZap className="w-5 h-5 text-[#7C3AED] mt-0.5" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${tone.dot} ${st.tone === "green" ? "animate-pulse" : ""}`} />
                        <span className={`font-display text-lg tracking-tight ${tone.text}`} data-testid="mcp-status-label">{tone.label}</span>
                    </div>
                    <div className="text-xs text-white/50 mt-1" data-testid="mcp-status-reason">{st.reason}</div>
                    <StatusFacts status={status} />
                </div>
                <div className="flex items-center gap-2">
                    {isSuper && status?.role === "hub" && (
                        <Link to="/connections" data-testid="mcp-manage-connections"
                            className="px-3 py-2 rounded-md text-xs border border-white/15 text-white/70 hover:text-white hover:bg-white/5 transition-colors">
                            Manage connections
                        </Link>
                    )}
                    <button onClick={onRefresh} data-testid="mcp-status-refresh" title="Refresh"
                        className="p-2 rounded-md border border-white/15 text-white/60 hover:text-white hover:bg-white/5 transition-colors">
                        <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                    {isSuper && status?.configured && (
                        <button onClick={runTest} disabled={busy} data-testid="mcp-test-list-databases"
                            className="px-3 py-2 rounded-md text-xs font-medium bg-white text-black hover:bg-white/90 disabled:opacity-50 transition-colors">
                            {busy ? "Testing…" : "Test: list databases"}
                        </button>
                    )}
                </div>
            </div>
            {test && <TestResult test={test} />}
        </div>
    );
}

function StatusFacts({ status }) {
    if (!status?.configured) return null;
    const c = status.connection || {};
    const conn = c.label ? `${c.label} · ${c.source === "delivered" ? "from the Radar hub" : "this hub"}` : null;
    const facts = [["Connection", conn], ["Server", status.url], ["Environment", status.env], ["Version", status.server ? `${status.server.name} ${status.server.version}` : null],
        ["Field map", status.field_map?.version ? `v${status.field_map.version}` : "nothing published"],
        ["Limits", `${status.limits.tool_calls} calls · ${status.limits.result_chars.toLocaleString()} chars · ${status.limits.documents} docs`]];
    return (
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-3 text-[11px] font-mono" data-testid="mcp-status-facts">
            {facts.filter(([, v]) => v).map(([k, v]) => <div key={k} className="truncate"><span className="text-white/35">{k}</span> <span className="text-white/70">{v}</span></div>)}
        </div>
    );
}

function TestResult({ test }) {
    return (
        <div className="mt-4 border-t border-white/10 pt-4" data-testid="mcp-test-result">
            <div className={`text-xs mb-2 ${test.ok ? "text-emerald-300" : "text-red-300"}`} data-testid="mcp-test-verdict">
                {test.ok ? "aiservice, eSource and regez are all visible." : "A required database is missing."}
            </div>
            <div className="flex flex-wrap gap-1.5">
                {Object.entries(test.expected).map(([db, ok]) => (
                    <span key={db} data-testid={`mcp-test-db-${db}`}
                        className={`flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded border ${ok ? "border-emerald-400/30 text-emerald-300" : "border-red-400/30 text-red-300"}`}>
                        {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {db}
                    </span>
                ))}
                {test.databases.some((d) => !(d in test.expected)) && (
                    <span className="text-[11px] font-mono px-2 py-1 text-white/35" title={test.databases.filter((d) => !(d in test.expected)).join(", ")}
                        data-testid="mcp-test-other-dbs">+{test.databases.filter((d) => !(d in test.expected)).length} other databases on the server (not offered)</span>
                )}
            </div>
        </div>
    );
}
